import {Button, Input, InputContainer} from "finform";
import {NotifyManager} from "notify-component";
import React, {Fragment, useState, useCallback} from 'react';
import {connect} from "react-redux";

import {Row} from "#app/components/layout/styles";
import {Caption} from "#app/components/texts/Typography";
import {handleCloseModal, register, requestRegister} from '#app/store/actions/auth';
import {handleTitle} from '#app/store/actions/interface';
import {Modal} from "src/interface/modal/Modal";

import {Container, GreyText, Text, Wrapper} from "./styles";
import {RegisterProps, RegisterForm, RegisterActionParams, FormErrors, RegisterOwnProps, RegisterReduxProps, RegisterReduxState} from "./types";

const Register: React.FC<RegisterProps> = ({
    register: registerAction,
    requestRegister: requestRegisterAction,
    handleTitle: _handleTitleAction,
    handleCloseModal,
    i18n,
    callback
}) => {
    const [form, setForm] = useState<RegisterForm>({
        email: '',
        password: '',
        password_confirmation: '',
        captchaValue: '',
        code: '',
        role: 'client',
        promo_code: '',
        form: 'client'
    });
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const [code, setCode] = useState(false);
    const [_submitted, setSubmitted] = useState(false);
    const [_checked, _setChecked] = useState(false);

    const getError = useCallback((fieldName: string): string | undefined => {
        if (formErrors && typeof formErrors[fieldName] !== 'undefined' && formErrors[fieldName]?.length && formErrors[fieldName]!.length > 0) {
            return formErrors[fieldName]![0];
        }
        return undefined;
    }, [formErrors]);

    const updateFormField = useCallback((field: keyof RegisterForm, value: string) => {
        setForm(prev => ({
            ...prev,
            [field]: value
        }));
        setFormErrors(prev => ({
            ...prev,
            [field]: undefined
        }));
    }, []);

    const submitRequestRegisterForm = useCallback(() => {
        setSubmitted(true);
        
        const params: RegisterActionParams = {
            form,
            success: (_response, _status, _xhr) => {
                setCode(true);
                setSubmitted(false);
                setFormErrors({});
            },
            error: (xhr) => {
                setSubmitted(false);
                // Устанавливаем ошибки из ответа сервера
                if (xhr?.response?.data?.errors) {
                    setFormErrors(xhr.response.data.errors);
                }
            },
            obj: undefined
        };
        requestRegisterAction(params);
    }, [form, requestRegisterAction]);

    const submitRegisterForm = useCallback(() => {
        setSubmitted(true);
        
        const params: RegisterActionParams = {
            form,
            success: (response, status, xhr) => {
                Modal.handleClose();

                const header = xhr.getResponseHeader('Authorization');

                if (header) {
                    localStorage.setItem('api_token', header);
                    handleCloseModal();

                    setForm({
                        email: '',
                        password: '',
                        password_confirmation: '',
                        captchaValue: '',
                        code: '',
                        role: 'client',
                        promo_code: '',
                        form: 'client'
                    });
                    setFormErrors({});
                    setSubmitted(false);
                    
                    if (typeof callback === 'function') {
                        callback();
                    }
                } else {
                    NotifyManager.error('Ошибка', 'Ошибка авторизации');
                }
            },
            error: (xhr) => {
                setSubmitted(false);
                // Устанавливаем ошибки из ответа сервера
                if (xhr?.response?.data?.errors) {
                    setFormErrors(xhr.response.data.errors);
                }
            },
            obj: undefined
        };
        registerAction(params);
    }, [form, registerAction, handleCloseModal, callback]);

    const submitForm = useCallback(() => {
        if (!form) return;
        
        // Валидация
        const errors: FormErrors = {};
        
        if (!form.email || !form.email.trim()) {
            errors.email = ['Email обязателен'];
        } else if (!form.email.includes('@')) {
            errors.email = ['Некорректный email'];
        } else if (form.email.length > 35) {
            errors.email = ['Адрес электронной почты слишком длинный (не более 35 символов)'];
        }
        
        if (!form.password || !form.password.trim()) {
            errors.password = ['Пароль обязателен'];
        } else if (form.password.length < 8) {
            errors.password = ['Пароль должен содержать не менее 8 символов'];
        }
        
        if (!form.password_confirmation || !form.password_confirmation.trim()) {
            errors.password_confirmation = ['Подтверждение пароля обязательно'];
        } else if (form.password !== form.password_confirmation) {
            errors.password_confirmation = ['Пароли не совпадают'];
        }
        
        if (code && (!form.code || !form.code.trim())) {
            errors.code = ['Код подтверждения обязателен'];
        }
        
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }
        
        setFormErrors({});
        
        if (code) {
            submitRegisterForm();
        } else {
            submitRequestRegisterForm();
        }
    }, [form, code, submitRegisterForm, submitRequestRegisterForm]);

    const handleEnter = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.keyCode === 13) {
            submitForm();
        }
    }, [submitForm]);

    const renderCodeForm = () => {
        return <Wrapper>
            <Container>
                <Fragment>
                    <Text className={`style__break-word`}>
                        {i18n.auth.codeAdvise} {form.email}
                    </Text>
                    <Row>
                        <InputContainer error={getError('code')}>
                            <Input
                                id='register-code'
                                type="text"
                                value={form.code || ''}
                                onKeyDown={handleEnter}
                                onChange={((value: string | number) => {
                                    updateFormField('code', String(value));
                                }) as any}
                                placeholder={i18n.auth.code}
                            />
                        </InputContainer>
                    </Row>
                </Fragment>
                <GreyText>{i18n.auth.issue}</GreyText>
                <Row>
                    <Button wide id={`register-submit`} onClick={submitForm}>
                        {i18n.auth.confirm}
                    </Button>
                </Row>
            </Container>
        </Wrapper>;
    };

    const renderRegisterForm = () => {
        return <Wrapper>
            <Container>
                <Row>
                    <InputContainer error={getError('email')}>
                        <Input
                            id='register-email'
                            type="text"
                            name="email"
                            autoComplete='on'
                            value={form.email}
                            onKeyDown={handleEnter}
                            onChange={((value: string | number) => {
                                updateFormField('email', String(value));
                            }) as any}
                            placeholder={'Email'}
                        />
                    </InputContainer>
                </Row>
                <Row>
                    <InputContainer error={getError('password')}>
                        <Input
                            id='register-password'
                            type="password"
                            name="password"
                            value={form.password}
                            onKeyDown={handleEnter}
                            onChange={((value: string | number) => {
                                updateFormField('password', String(value));
                            }) as any}
                            placeholder={i18n.auth.password}
                        />
                    </InputContainer>
                </Row>
                <Row>
                    <InputContainer error={getError('password_confirmation')}>
                        <Input
                            id='register-password_confirmation'
                            type="password"
                            name="password_confirmation"
                            value={form.password_confirmation}
                            onKeyDown={handleEnter}
                            onChange={((value: string | number) => {
                                updateFormField('password_confirmation', String(value));
                            }) as any}
                            placeholder={i18n.auth.passwordRepeat}
                        />
                    </InputContainer>
                </Row>
                <Row>
                    <InputContainer error={getError('promo_code')}>
                        <Input
                            id='register-promo_code'
                            type="text"
                            name="promo_code"
                            value={form.promo_code || ''}
                            onKeyDown={handleEnter}
                            onChange={((value: string | number) => {
                                updateFormField('promo_code', String(value));
                            }) as any}
                            placeholder={i18n.auth.promoCode}
                        />
                    </InputContainer>
                </Row>
                <Row style={{margin: '10px 0'}}>
                    <Caption white>Регистрируясь на сайте Вы соглашаетесь с <a target="_blank" href="/agreement"
                                                                               style={{color: '#4378FF'}}>Пользовательским
                        соглашением</a> и <a target="_blank" href="/policy" style={{color: '#4378FF'}}>Политикой по
                        обработке персональных данных</a>.</Caption>
                </Row>
                <Row>
                    <Button wide id='register-submit' onClick={submitForm}>{i18n.auth.toRegister}</Button>
                </Row>
            </Container>
        </Wrapper>;
    };

    if (code) {
        return renderCodeForm();
    } else {
        return renderRegisterForm();
    }
};

const enhance = connect<RegisterReduxState, RegisterReduxProps, RegisterOwnProps, RegisterReduxState>(
    (state) => ({
        i18n: state.i18n,
    }),
    {register, requestRegister, handleTitle, handleCloseModal} as any
);

export default enhance(Register);
